import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useMemo, useState, useEffect } from 'react'
import { useRemoteConfig } from '@/hooks/useRemoteConfig'
import { useMutateUserSettings } from '@/services'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { eventTracker } from '@/config/trackingFunctions'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { useLocation } from 'react-router-dom'

export const TOSModal = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [tosChecked, setTosChecked] = useState(false)
  const [privacyChecked, setPrivacyChecked] = useState(false)
  const [cookieChecked, setCookieChecked] = useState(false)
  const location = useLocation()

  const allAccepted = tosChecked && privacyChecked && cookieChecked

  const { getString, isReady } = useRemoteConfig()
  const { mutate: mutateUserSettings, isLoading } = useMutateUserSettings()
  const { user } = useAppSelector(userSelector)
  const requiredTosAcceptTime = getString('requiredTosAcceptTime', '0')

  const shouldShow = useMemo(() => {
    const lastTosAcceptTime = user?.userPreferences?.lastTosAcceptTime ?? '0'

    if (isReady) {
      if (requiredTosAcceptTime !== '0' && Number(lastTosAcceptTime) < Number(requiredTosAcceptTime)) {
        return true
      }
    }

    return false
  }, [isReady, requiredTosAcceptTime, user?.userPreferences?.lastTosAcceptTime])

  useEffect(() => {
    if (shouldShow) {
      setIsOpen(true)
    }
  }, [shouldShow])

  const handleAccept = () => {
    const terms = {
      lastTosAcceptTime: String(Math.floor(Date.now() / 1000)),
    }

    mutateUserSettings(terms, {
      onSuccess: () => {
        eventTracker(TRACKING_TYPES.UPDATED_TOS_ACCEPTED)
        setIsOpen(false)
      },
      onError: (e: any) => {
        console.error('TOS persistence error', e)
      },
    })
  }

  if (location.pathname === '/membership-required') {
    return null
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => null}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-[640px] flex flex-col h-screen md:h-auto transform overflow-y-auto md:rounded-[32px] pt-6 md:py-4 pb-4 bg-white text-left align-middle shadow-xl transition-all relative">
                <div className="flex items-center justify-between px-4">
                  <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold text-center p-2">
                    Notice of Updated Policies
                  </Dialog.Title>
                  <div className="w-9 h-9" />
                </div>

                <div className="flex flex-col p-6 items-center justify-center">
                  <p className="mb-6 text-gray-700">
                    We’ve updated our Terms of Service, Privacy Policy and Cookie Policy. Please review and accept each
                    policy to continue using your account.
                  </p>

                  <div className="space-y-4 mb-6">
                    <label className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={tosChecked}
                        onChange={(e) => setTosChecked(e.target.checked)}
                      />
                      <span>
                        I have read and agree to the{' '}
                        <a
                          href="https://www.dupr.com/terms"
                          className="text-blue-600 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms of Service
                        </a>
                      </span>
                    </label>

                    <label className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={privacyChecked}
                        onChange={(e) => setPrivacyChecked(e.target.checked)}
                      />
                      <span>
                        I have read, acknowledge and understand the{' '}
                        <a
                          href="https://www.dupr.com/privacy-policy"
                          className="text-blue-600 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Privacy Policy
                        </a>
                      </span>
                    </label>

                    <label className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={cookieChecked}
                        onChange={(e) => setCookieChecked(e.target.checked)}
                      />
                      <span>
                        I have read and consent to the use of cookies as described in the{' '}
                        <a
                          href="https://www.dupr.com/cookie-policy"
                          className="text-blue-600 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Cookie Policy
                        </a>
                      </span>
                    </label>
                  </div>

                  <button
                    onClick={handleAccept}
                    disabled={!allAccepted || isLoading}
                    className={`w-full py-2 rounded-lg font-semibold transition-colors ${
                      allAccepted
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Accept
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default TOSModal
